// Turns the backend's finished First 5 Days / #Ad cells into what the table shows.
import type { AdDisclosureCell, FirstFiveDaysReason, FirstFiveDaysViews } from '@discovr/types';
import { formatIntegerIN } from '../lib/format';
import { lookupOr } from '../lib/lookupOr';

const DASH = '—';
const NOT_AVAILABLE = 'Not available';
const LOCKED_HINT = 'Views at the check closest to 5 days after the post went up.';
const UNKNOWN_HINT = 'Shows once the post’s publish time is known.';
const LOCKS_NEXT_CHECK = 'locks at the next check';
const AD_UNKNOWN_HINT = 'Not checked yet.';
const AD_NO_HINT = 'No paid partnership label or disclosure hashtag found.';
const AD_NOT_FOUND_HINT =
  'No disclosure hashtag in the title or description. YouTube’s own paid-promotion label can’t be checked.';
const AD_YES_HINT = 'Disclosure found.';
const AD_MATCHED_PREFIX = 'Found: ';
const AD_SUPPORTING_PREFIX = 'Also seen: ';
const HASHTAG_SIGNAL_PREFIX = 'hashtag:';

const REASON_HINT: Record<FirstFiveDaysReason, string> = {
  no_reading_near_day_5:
    'No stats check landed near day 5 (for example, the post was marked live late).',
  no_view_count: 'Instagram photo and carousel posts have no view count.',
  before_tracking: 'Day 5 passed before this column was added.',
};
const UNKNOWN_REASON_HINT = 'No day-5 value can be recorded for this post.';

const SIGNAL_LABEL: Record<string, string> = {
  paid_partnership_label: 'Paid partnership label',
  brand_coauthor: 'Brand added as co-author',
};
// A brand co-author is supporting evidence only, never why the answer is Yes.
const SUPPORTING_SIGNALS: ReadonlySet<string> = new Set(['brand_coauthor']);

const LOCK_TIME_FORMAT = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
});

export interface FirstFiveDaysDisplay {
  text: string;
  /** "Day 3 of 5" while the value isn't final. */
  progressTag?: string;
  hint: string;
  muted: boolean;
}

export function firstFiveDaysDisplay(
  cell: FirstFiveDaysViews | undefined,
  now: Date,
): FirstFiveDaysDisplay {
  switch (cell?.state) {
    case 'locked':
      return { text: formatIntegerIN(cell.views), hint: LOCKED_HINT, muted: false };
    case 'in_progress':
      return {
        text: formatIntegerIN(cell.views),
        progressTag: progressTagText(cell.day, cell.days_total),
        hint: `Views so far. Not final — ${lockTimeText(cell.locks_at, now)}.`,
        muted: true,
      };
    case 'unavailable':
      return {
        text: NOT_AVAILABLE,
        hint: lookupOr(REASON_HINT, cell.reason, UNKNOWN_REASON_HINT),
        muted: true,
      };
    default:
      return { text: DASH, hint: UNKNOWN_HINT, muted: true };
  }
}

// An older backend may leave out the day counter; show no tag rather than "Day undefined".
function progressTagText(
  day: number | undefined,
  daysTotal: number | undefined,
): string | undefined {
  if (day === undefined || daysTotal === undefined) return undefined;
  return `Day ${day} of ${daysTotal}`;
}

// Past day 5 the value locks on the next stats check, not at the day-5 moment. A
// missing or unreadable lock time reads the same rather than throwing in render.
function lockTimeText(locksAt: string | undefined, now: Date): string {
  const at = new Date(locksAt ?? '');
  if (Number.isNaN(at.getTime()) || at.getTime() <= now.getTime()) return LOCKS_NEXT_CHECK;
  return `locks ${LOCK_TIME_FORMAT.format(at)}`;
}

export type AdTone = 'yes' | 'no' | 'not_found' | 'unknown';

export interface AdMentionedDisplay {
  label: string;
  hint: string;
  tone: AdTone;
}

export function adMentionedDisplay(cell: AdDisclosureCell | undefined): AdMentionedDisplay {
  const signals = cell?.signals ?? [];
  switch (cell?.state) {
    case 'yes':
      return { label: 'Yes', hint: describeSignals(signals), tone: 'yes' };
    case 'no':
      return { label: 'No', hint: withSupporting(AD_NO_HINT, signals), tone: 'no' };
    case 'not_found':
      return { label: 'Not found', hint: AD_NOT_FOUND_HINT, tone: 'not_found' };
    default:
      return { label: DASH, hint: AD_UNKNOWN_HINT, tone: 'unknown' };
  }
}

function signalLabel(signal: string): string {
  if (signal.startsWith(HASHTAG_SIGNAL_PREFIX)) {
    return `#${signal.slice(HASHTAG_SIGNAL_PREFIX.length)}`;
  }
  return lookupOr(SIGNAL_LABEL, signal, signal);
}

function describeSignals(signals: string[]): string {
  const matched = signals.filter((s) => !SUPPORTING_SIGNALS.has(s)).map(signalLabel);
  const base = matched.length > 0 ? `${AD_MATCHED_PREFIX}${matched.join(', ')}.` : AD_YES_HINT;
  return withSupporting(base, signals);
}

function withSupporting(base: string, signals: string[]): string {
  const supporting = signals.filter((s) => SUPPORTING_SIGNALS.has(s)).map(signalLabel);
  if (supporting.length === 0) return base;
  return `${base} ${AD_SUPPORTING_PREFIX}${supporting.join(', ')}.`;
}
